import { Box, Tooltip } from "@mui/material";
import { useIntl } from "react-intl";
import { ImportJobResultCounts } from "services/KnowfullyAdminClient";

const styles = {
  cell: {
    display: "grid",
    gap: 0.5,
    minWidth: 170,
  },
  bar: {
    display: "flex",
    height: 6,
    borderRadius: 3,
    overflow: "hidden",
    backgroundColor: "action.hover",
  },
  legend: {
    fontSize: "0.7rem",
    color: "text.secondary",
    fontVariantNumeric: "tabular-nums",
  },
  empty: {
    fontSize: "0.7rem",
    color: "text.disabled",
  },
};

type Segment = {
  key: string;
  value: number;
  color: string;
  labelKey: string;
};

export default function ImportsResultsCell({
  counts,
}: Readonly<{ counts?: ImportJobResultCounts }>) {
  const { formatMessage: t } = useIntl();

  const total = counts?.total ?? 0;

  if (!counts || total === 0) {
    return <Box sx={styles.empty}>{t({ id: "notSplitYetLabel" })}</Box>;
  }

  const segments: Segment[] = [
    {
      key: "processed",
      value: counts.processed ?? 0,
      color: "success.main",
      labelKey: "processedLabel",
    },
    {
      key: "errored",
      value: counts.errored ?? 0,
      color: "error.main",
      labelKey: "erroredLabel",
    },
    {
      key: "inProgress",
      value: counts.inProgress ?? 0,
      color: "info.main",
      labelKey: "inProgressLabel",
    },
    {
      key: "pending",
      value: counts.pending ?? 0,
      color: "action.disabled",
      labelKey: "pendingLabel",
    },
    {
      key: "cancelled",
      value: counts.cancelled ?? 0,
      color: "text.disabled",
      labelKey: "cancelledLabel",
    },
  ];

  const present = segments.filter((s) => s.value > 0);

  const legend = present
    .map((s) => `${s.value} ${t({ id: s.labelKey }).toLowerCase()}`)
    .join(" · ");

  return (
    <Box sx={styles.cell}>
      <Tooltip title={`${total} ${t({ id: "totalLabel" }).toLowerCase()}`}>
        <Box sx={styles.bar}>
          {present.map((segment) => (
            <Box
              key={segment.key}
              sx={{
                backgroundColor: segment.color,
                width: `${(segment.value / total) * 100}%`,
              }}
            />
          ))}
        </Box>
      </Tooltip>
      <Box sx={styles.legend}>{legend}</Box>
    </Box>
  );
}
